import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Stack, Text, Icon, IconButton, Shimmer, ShimmerElementType, MessageBar, MessageBarType, TooltipHost } from "@fluentui/react";
import { Sparkline } from "@fluentui/react-charting";
import { CUSTOMER_HEALTH_STATUS_COLORS, PurviewCustomerHealthTooltips, resolveIndicatorTooltip } from "./CustomerHealthView.constants";
import PurviewDeploymentRawIndicatorCards from "./PurviewDeploymentRawIndicatorCards";
import PurviewEngagementRawIndicatorCards from "./PurviewEngagementRawIndicatorCards";
import PurviewChurnRawIndicatorCards from "./PurviewChurnRawIndicatorCards";
import PurviewCommunityEngagementRawIndicatorCards from "./PurviewCommunityEngagementRawIndicatorCards";
import SHIScoreRawIndicatorCards from "./SHIScoreRawIndicatorCards";
import FeatureHealthRawIndicatorCards from "./FeatureHealthRawIndicatorCards";
import {
  useCustomerHealthData,
  useCustomerHealthProductTrendData,
  HealthViewMode
} from "./useCustomerHealthHooks";

interface PurviewProductDetailsProps {
  tenantId: string;
  viewMode?: HealthViewMode;
}

export interface PurviewProductDetailsHandle {
  selectCell: (product: string, indicator: string) => void;
}

interface IndicatorMetric {
  label: string;
  value: number | string;
}

interface IndicatorCard {
  title: string;
  metrics: IndicatorMetric[];
}

const formatPascalCase = (text: string): string => {
  if (text === "ChurnRate4Months") return "4 Months Churn Rate";
  if (/^feature\s*request\s*health$/i.test(text)) return "Feature Health Score";
  return text.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
};

const getValueColor = (label: string, value: number) => {
  if (label.toLowerCase() !== "score") return undefined;
  if (value >= 71) return CUSTOMER_HEALTH_STATUS_COLORS.Healthy;
  if (value >= 41) return CUSTOMER_HEALTH_STATUS_COLORS.Warning;
  return CUSTOMER_HEALTH_STATUS_COLORS.Critical;
};

// Maps heat map short product names → product card full names
const HEATMAP_TO_PRODUCT_NAME: Record<string, string> = {
  "Audit": "Microsoft Purview Audit",
  "CC": "Microsoft Purview Communication Compliance",
  "CM": "Microsoft Purview Compliance Manager",
  "DLM": "Microsoft Purview Data Lifecycle Management",
  "DLP": "Microsoft Purview Data Loss Prevention",
  "eDiscovery": "Microsoft Purview eDiscovery",
  "IRM": "Microsoft Purview Insider Risk Management",
  "MIP": "Microsoft Purview Information Protection",
};

const PurviewProductDetails = React.forwardRef<PurviewProductDetailsHandle, PurviewProductDetailsProps>(({ tenantId, viewMode = "tenant" }, ref) => {
  const entityLabel = viewMode === "tpid" ? "TPID" : "tenant";
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [selectedIndicator, setSelectedIndicator] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const indicatorScrollRef = useRef<HTMLDivElement>(null);
  const [isIndicatorOverflowing, setIsIndicatorOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    selectCell: (product: string, indicator: string) => {
      const mappedProduct = HEATMAP_TO_PRODUCT_NAME[product] ?? product;
      setSelectedProduct(mappedProduct);
      setSelectedIndicator(indicator);
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        if (scrollContainerRef.current) {
          const productCard = scrollContainerRef.current.querySelector(
            `[data-product="${CSS.escape(mappedProduct)}"]`
          ) as HTMLElement | null;
          productCard?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        }
      }, 100);
      setTimeout(() => {
        if (indicatorScrollRef.current) {
          const indicatorCard = indicatorScrollRef.current.querySelector(
            `[data-indicator="${CSS.escape(indicator)}"]`
          ) as HTMLElement | null;
          indicatorCard?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        }
      }, 300);
    }
  }));

  const checkIndicatorOverflow = useCallback(() => {
    const el = indicatorScrollRef.current;
    if (el) {
      setIsIndicatorOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, []);

  const scrollBy = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const amount = direction === "left" ? -240 : 240;
      scrollContainerRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  const scrollIndicatorsBy = (direction: "left" | "right") => {
    if (indicatorScrollRef.current) {
      const amount = direction === "left" ? -280 : 280;
      indicatorScrollRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  const { data: trendData, isLoading: trendLoading, isError: trendError } =
    useCustomerHealthProductTrendData(tenantId, "Purview", 6, viewMode);

  const { data: healthResponse, isLoading: healthLoading, isError: healthError } =
    useCustomerHealthData(tenantId, "Purview", viewMode);

  const healthData = healthResponse?.customerHealthDataList ?? healthResponse;

  const products = useMemo(() => {
    if (!trendData || !healthData) return [];

    // Score from healthData where subCategory1 === "CustomerHealthScore"
    const scoreMap: Record<string, number> = {};
    for (const item of healthData) {
      if (
        item.category === "Indicator" &&
        item.subCategory1 === "CustomerHealthScore" &&
        item.productName !== "Overall" &&
        !(item.productName in scoreMap)
      ) {
        scoreMap[item.productName] = Number(item.value);
      }
    }

    const dateMap: Record<string, { date: string; score: number }[]> = {};
    const trendSeen = new Set<string>();
    for (const item of trendData) {
      const name = item.productName;
      if (name === "Overall") continue;
      const key = `${name}|${item.factDate}`;
      if (trendSeen.has(key)) continue;
      trendSeen.add(key);
      if (!dateMap[name]) dateMap[name] = [];
      dateMap[name].push({ date: item.factDate, score: Number(item.customerHealthScore) });
    }

    return Object.entries(dateMap).map(([name, entries]) => {
      const sorted = [...entries].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      const trendScores = sorted.map((e) => e.score);
      const score = scoreMap[name] ?? trendScores[trendScores.length - 1];

      let scoreColor: string;
      if (score >= 71) scoreColor = CUSTOMER_HEALTH_STATUS_COLORS.Healthy;
      else if (score >= 41) scoreColor = CUSTOMER_HEALTH_STATUS_COLORS.Warning;
      else scoreColor = CUSTOMER_HEALTH_STATUS_COLORS.Critical;

      const latest = trendScores[trendScores.length - 1];
      const previous = trendScores.length >= 2 ? trendScores[trendScores.length - 2] : latest;
      let sparklineColor: string;
      if (latest < previous) sparklineColor = CUSTOMER_HEALTH_STATUS_COLORS.Critical;
      else if (latest > previous) sparklineColor = CUSTOMER_HEALTH_STATUS_COLORS.Healthy;
      else sparklineColor = CUSTOMER_HEALTH_STATUS_COLORS.Warning;

      const delta = latest - previous;
      return { name, score, trend: trendScores, scoreColor, sparklineColor, delta };
    });
  }, [trendData, healthData]);

  const indicators = useMemo((): IndicatorCard[] => {
    if (!healthData || !selectedProduct) return [];

    // Filter: matching product, category=Indicator, exclude CustomerHealthScore
    const filtered = healthData.filter(
      (item: any) =>
        item.productName === selectedProduct &&
        item.category === "Indicator" &&
        item.subCategory1 !== "CustomerHealthScore"
    );

    // Deduplicate by subCategory1 + subCategory2
    const seen = new Set<string>();
    const deduped = filtered.filter((item: any) => {
      const key = `${item.subCategory1}|${item.subCategory2}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Group by subCategory1 (indicator name), collect subCategory2 as metric labels
    const grouped: Record<string, IndicatorMetric[]> = {};
    for (const item of deduped) {
      const indicatorName = item.subCategory1;
      if (!indicatorName) continue;
      if (!grouped[indicatorName]) grouped[indicatorName] = [];

      let label = item.subCategory2;
      let value = item.value;

      if (label === "Weight Percentage") {
        label = "Weight";
        value = `${value}%`;
      } else if (label === "Contribution Points") {
        label = "Contribution";
        value = `${value} pts`;
      }

      grouped[indicatorName].push({ label, value });
    }

    const metricSortOrder: Record<string, number> = { "Score": 0, "Weight": 1, "Contribution": 2 };

    const indicatorSortOrder: Record<string, number> = {
      "deployment": 1,
      "engagement": 2,
      "product engagement": 2,
      "churn": 3,
      "community engagement": 4,
      "feature request health": 5,
      "feature health score": 5,
      "support health score": 6,
      "shi score": 6,
    };

    const getIndicatorOrder = (title: string): number => {
      const display = formatPascalCase(title).toLowerCase();
      for (const [key, order] of Object.entries(indicatorSortOrder)) {
        if (display.includes(key)) return order;
      }
      return 99;
    };

    return Object.entries(grouped)
      .map(([title, metrics]) => ({
        title,
        metrics: metrics.sort((a, b) => (metricSortOrder[a.label] ?? 99) - (metricSortOrder[b.label] ?? 99)),
      }))
      .sort((a, b) => getIndicatorOrder(a.title) - getIndicatorOrder(b.title));
  }, [healthData, selectedProduct]);

  useEffect(() => {
    checkIndicatorOverflow();
    window.addEventListener("resize", checkIndicatorOverflow);
    return () => window.removeEventListener("resize", checkIndicatorOverflow);
  }, [checkIndicatorOverflow, indicators]);

  useEffect(() => {
    if (products.length > 0 && !selectedProduct) {
      setSelectedProduct(products[0].name);
    }
  }, [products]);

  useEffect(() => {
    if (indicators.length > 0 && !selectedIndicator) {
      setSelectedIndicator(indicators[0].title);
    }
  }, [indicators]);

  const rawData = useMemo((): { label: string; value: number | string }[] => {
    if (!healthData || !selectedProduct || !selectedIndicator) return [];

    const resolveRawLabel = (item: any): string => {
      const candidates = [
        item.subCategory4,
        item.subCategory2,
        item.subCategory3,
        item.subCategory1
      ];

      for (const candidate of candidates) {
        const text = String(candidate ?? "").trim();
        if (text) return text;
      }

      return "Metric";
    };

    const seen = new Set<string>();
    return healthData.filter((item: any) => {
      const indicatorInSubCategory1 = item.subCategory1 === selectedIndicator;
      const indicatorInSubCategory3 = item.subCategory3 === selectedIndicator;
      if (
        item.productName !== selectedProduct ||
        item.category !== "RawData" ||
        (!indicatorInSubCategory1 && !indicatorInSubCategory3)
      ) return false;
      const label = resolveRawLabel(item);
      const key = `${selectedIndicator}|${String(label)}|${String(item.value)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map((item: any) => {
      let value = String(item.value);
      if (item.subCategory2 === "IsEngaged") {
        value = Number(value) === 1 ? "Yes" : "No";
      } else if (value.trim() !== "" && !isNaN(Number(value))) {
        value = Number(value).toLocaleString();
      }
      return { label: resolveRawLabel(item), value };
    });
  }, [healthData, selectedProduct, selectedIndicator]);

  const rawIndicatorRows = useMemo(() => {
    if (!healthData || !selectedProduct || !selectedIndicator) return [];
    const seen = new Set<string>();
    return (healthData as any[])
      .filter((item: any) => {
        if (
          item.productName !== selectedProduct ||
          item.category !== "RawData" ||
          item.subCategory1 !== selectedIndicator ||
          !item.subCategory3
        ) return false;
        const key = `${item.subCategory2}|${item.subCategory3}|${item.subCategory4}|${item.value}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((item: any) => ({
        sectionName: item.subCategory2,
        metricName: item.subCategory3,
        factorLabel: item.subCategory4,
        value: item.value,
      }));
  }, [healthData, selectedProduct, selectedIndicator]);

  const shiRows = useMemo(() => {
    if (!healthData || !selectedProduct || !/support.health|shi.score/i.test(selectedIndicator ?? "")) return [];
    const seen = new Set<string>();
    return (healthData as any[])
      .filter((item: any) => {
        if (
          item.productName !== selectedProduct ||
          item.category !== "RawData" ||
          !/support.health|shi.score/i.test(item.subCategory1 ?? "") ||
          !item.subCategory3
        ) return false;
        const key = `${item.subCategory2}|${item.subCategory3}|${item.subCategory4}|${item.value}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((item: any) => ({
        sectionName: item.subCategory2,
        metricName: item.subCategory3,
        factorLabel: item.subCategory4,
        value: item.value,
      }));
  }, [healthData, selectedProduct, selectedIndicator]);

  const featureHealthRows = useMemo(() => {
    if (!healthData || !selectedProduct || !/feature.health|feature.request/i.test(selectedIndicator ?? "")) return [];
    const seen = new Set<string>();
    return (healthData as any[])
      .filter((item: any) => {
        if (
          item.productName !== selectedProduct ||
          item.category !== "RawData" ||
          !/feature.health|feature.request/i.test(item.subCategory1 ?? "") ||
          !item.subCategory3
        ) return false;
        const key = `${item.subCategory2}|${item.subCategory3}|${item.subCategory4}|${item.value}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((item: any) => ({
        sectionName: /^feature\s*request\s*health$/i.test(item.subCategory2 ?? "")
          ? "Feature Health Score"
          : item.subCategory2,
        metricName: item.subCategory3,
        factorLabel: item.subCategory4,
        value: item.value,
      }));
  }, [healthData, selectedProduct, selectedIndicator]);

  if (trendLoading || healthLoading) {
    return (
      <Stack tokens={{ childrenGap: 32 }}>
        {/* Product Cards shimmer */}
        <Stack tokens={{ childrenGap: 12 }}>
          <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 200, height: 18 }]} />
          <Stack horizontal tokens={{ childrenGap: 12 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Stack key={i} tokens={{ childrenGap: 8 }} styles={{ root: { width: 240, minWidth: 240, padding: 16, border: "1px solid #EDEBE9", borderRadius: 8 } }}>
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: "80%", height: 16 }]} />
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 60, height: 24 }]} />
                <div style={{ height: 1, backgroundColor: "#EDEBE9", marginTop: 8, marginBottom: 4 }} />
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: "100%", height: 30 }]} />
              </Stack>
            ))}
          </Stack>
        </Stack>

        {/* Indicator Cards shimmer */}
        <Stack tokens={{ childrenGap: 12 }}>
          <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 160, height: 18 }]} />
          <Stack horizontal tokens={{ childrenGap: 12 }}>
            {[0, 1, 2, 3].map((i) => (
              <Stack key={i} tokens={{ childrenGap: 12 }} styles={{ root: { flex: "1 1 0", minWidth: 180, padding: 16, border: "1px solid #EDEBE9", borderRadius: 8 } }}>
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: "70%", height: 16 }]} />
                <Stack horizontal tokens={{ childrenGap: 24 }}>
                  {[0, 1, 2].map((j) => (
                    <Stack key={j} tokens={{ childrenGap: 4 }}>
                      <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 50, height: 12 }]} />
                      <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 40, height: 16 }]} />
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>

        {/* Raw Data shimmer */}
        <Stack tokens={{ childrenGap: 12 }}>
          <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 120, height: 18 }]} />
          <Stack horizontal tokens={{ childrenGap: 12 }}>
            {[0, 1, 2, 3].map((i) => (
              <Stack key={i} tokens={{ childrenGap: 8 }} styles={{ root: { width: 200, minWidth: 200, padding: 16, border: "1px solid #EDEBE9", borderRadius: 8 } }}>
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: "80%", height: 14 }]} />
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, width: 60, height: 24 }]} />
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>
    );
  }

  if (trendError || healthError) {
    return (
      <MessageBar messageBarType={MessageBarType.error}>
        Failed to load product data. Please refresh the page or try again later.
      </MessageBar>
    );
  }

  if (products.length === 0) {
    return (
      <MessageBar messageBarType={MessageBarType.warning}>
        No product data available for this {entityLabel}.
      </MessageBar>
    );
  }

  return (
    <div ref={containerRef}>
    <Stack tokens={{ childrenGap: 32 }}>
      {/* Product Cards */}
      <Stack tokens={{ childrenGap: 12 }}>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
            Purview - Products
          </Text>
          <TooltipHost content={PurviewCustomerHealthTooltips.ProductsHeader}>
            <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
          </TooltipHost>
          <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
            {products.length} Products. Equal weight (Click any Product card to view indicators and raw data contributing to the score)
          </Text>
        </Stack>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }} styles={{ root: { width: "100%" } }}>
          <IconButton
            iconProps={{ iconName: "ChevronLeft" }}
            onClick={() => scrollBy("left")}
            styles={{ root: { minWidth: 32, height: 32, flexShrink: 0 } }}
            ariaLabel="Scroll left"
          />
          <div
            ref={scrollContainerRef}
            style={{
              display: "flex",
              gap: 12,
              overflowX: "auto",
              scrollbarWidth: "none",
              flex: 1,
              padding: "4px 0",
            }}
          >
          {products.map((product) => {
            const isSelected = selectedProduct === product.name;
            return (
              <Stack
                key={product.name}
                data-product={product.name}
                tokens={{ childrenGap: 8 }}
                onClick={() => {
                  setSelectedProduct(product.name);
                  setSelectedIndicator(null);
                }}
                styles={{
                  root: {
                    padding: "16px",
                    border: isSelected ? "2px solid #0078D4" : "1px solid #EDEBE9",
                    borderLeftWidth: 3,
                    borderLeftColor: isSelected ? "#0078D4" : "#EDEBE9",
                    borderRadius: 8,
                    backgroundColor: "#FFFFFF",
                    width: 240,
                    minWidth: 240,
                    flexShrink: 0,
                    overflow: "hidden",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    ":hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.12)", borderLeftColor: "#0078D4", backgroundColor: "#F5F5F5" },
                  },
                }}
              >
                <Stack horizontal horizontalAlign="space-between" verticalAlign="start" styles={{ root: { minHeight: 40 } }}>
                  <Text variant="medium" styles={{ root: { fontWeight: 600, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", lineHeight: "20px" } }}>
                    {product.name}
                  </Text>
                  <TooltipHost content={PurviewCustomerHealthTooltips.Products[product.name] ?? product.name}>
                    <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
                  </TooltipHost>
                </Stack>

                <Text variant="xLarge" styles={{ root: { fontWeight: 700, color: product.scoreColor } }}>
                  {product.score}
                </Text>

                <div style={{ height: 1, backgroundColor: "#EDEBE9", marginTop: 8, marginBottom: 4 }} />

                <Stack horizontal verticalAlign="center" horizontalAlign="space-between" tokens={{ childrenGap: 8 }}>
                  <Sparkline
                    data={{
                      chartTitle: `${product.name} Trend`,
                      lineChartData: [
                        {
                          legend: `${product.name} Score Trend`,
                          color: product.sparklineColor,
                          data: product.trend.map((value, index) => ({ x: index + 1, y: value })),
                        },
                      ],
                    }}
                    width={120}
                    height={30}
                    showLegend={false}
                  />
                  <Stack horizontalAlign="center" tokens={{ childrenGap: 2 }}>
                    <Text
                      variant="mediumPlus"
                      styles={{
                        root: {
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          color: product.delta > 0
                            ? CUSTOMER_HEALTH_STATUS_COLORS.Healthy
                            : product.delta < 0
                              ? CUSTOMER_HEALTH_STATUS_COLORS.Critical
                              : CUSTOMER_HEALTH_STATUS_COLORS.Warning,
                        },
                      }}
                    >
                      {product.delta > 0
                        ? `▲ +${product.delta} pts`
                        : product.delta < 0
                          ? `▼ ${product.delta} pts`
                          : "No change"}
                    </Text>
                    <Text variant="tiny" styles={{ root: { color: "#605E5C" } }}>vs prev. month</Text>
                  </Stack>
                </Stack>
              </Stack>
            );
          })}
          </div>
          <IconButton
            iconProps={{ iconName: "ChevronRight" }}
            onClick={() => scrollBy("right")}
            styles={{ root: { minWidth: 32, height: 32, flexShrink: 0 } }}
            ariaLabel="Scroll right"
          />
        </Stack>
      </Stack>

      {/* Indicator Cards */}
      {selectedProduct && indicators.length > 0 && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Indicators
            </Text>
            <TooltipHost content={PurviewCustomerHealthTooltips.Indicators}>
              <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            </TooltipHost>
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
              Weighted contribution to product score (Click any Indicator card to view raw data &amp; methodology)
            </Text>
          </Stack>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }} styles={{ root: { width: "100%" } }}>
            {isIndicatorOverflowing && (
              <IconButton
                iconProps={{ iconName: "ChevronLeft" }}
                onClick={() => scrollIndicatorsBy("left")}
                styles={{ root: { minWidth: 32, height: 32, flexShrink: 0 } }}
                ariaLabel="Scroll indicators left"
              />
            )}
            <div
              ref={indicatorScrollRef}
              style={{
                display: "flex",
                gap: 12,
                overflowX: isIndicatorOverflowing ? "auto" : "hidden",
                scrollbarWidth: "none",
                flex: 1,
                padding: "4px 0",
                flexWrap: "nowrap",
              }}
            >
              {indicators.map((indicator) => (
                <Stack
                  key={indicator.title}
                  data-indicator={indicator.title}
                  tokens={{ childrenGap: 12 }}
                  onClick={() => setSelectedIndicator(indicator.title)}
                  styles={{
                    root: {
                      padding: "16px",
                      border: selectedIndicator === indicator.title ? "2px solid #0078D4" : "1px solid #EDEBE9",
                      borderLeftWidth: 3,
                      borderLeftColor: selectedIndicator === indicator.title ? "#0078D4" : "#EDEBE9",
                      borderRadius: 8,
                      backgroundColor: "#FFFFFF",
                      width: 260,
                      minWidth: 260,
                      flexShrink: 0,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      ":hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.12)", borderLeftColor: "#0078D4", backgroundColor: "#F5F5F5" },
                    },
                  }}
                >
                  <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }} styles={{ root: { minHeight: 40 } }}>
                    <Text variant="medium" styles={{ root: { fontWeight: 600, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", lineHeight: "20px" } }}>
                      {formatPascalCase(indicator.title)}
                    </Text>
                    <TooltipHost content={resolveIndicatorTooltip(indicator.title, PurviewCustomerHealthTooltips.IndicatorDescriptions) ?? formatPascalCase(indicator.title)}>
                      <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
                    </TooltipHost>
                  </Stack>
                  <Stack horizontal tokens={{ childrenGap: 24 }}>
                    {indicator.metrics.map((metric, idx) => {
                      const numVal = Number(metric.value);
                      const color = getValueColor(metric.label, numVal);
                      return (
                        <Stack key={`${metric.label}-${idx}`} tokens={{ childrenGap: 4 }}>
                          <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
                            {metric.label}
                          </Text>
                          <Text variant="medium" styles={{ root: { fontWeight: 600, color: color || "inherit" } }}>
                            {metric.value}
                          </Text>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Stack>
              ))}
            </div>
            {isIndicatorOverflowing && (
              <IconButton
                iconProps={{ iconName: "ChevronRight" }}
                onClick={() => scrollIndicatorsBy("right")}
                styles={{ root: { minWidth: 32, height: 32, flexShrink: 0 } }}
                ariaLabel="Scroll indicators right"
              />
            )}
          </Stack>
        </Stack>
      )}

      {/* Deployment Raw Indicator */}
      {viewMode !== "tpid" && selectedIndicator && rawIndicatorRows.length > 0 && selectedIndicator.toLowerCase() === "deployment" && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Raw Data</Text>
            <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Deployment breakdown for selected product</Text>
          </Stack>
          <PurviewDeploymentRawIndicatorCards rows={rawIndicatorRows} />
        </Stack>
      )}

      {/* Engagement Raw Indicator */}
      {viewMode !== "tpid" && selectedIndicator && rawIndicatorRows.length > 0 && selectedIndicator.toLowerCase() === "engagement" && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Raw Data</Text>
            <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Product engagement breakdown</Text>
          </Stack>
          <PurviewEngagementRawIndicatorCards rows={rawIndicatorRows} />
        </Stack>
      )}

      {/* Churn Raw Indicator */}
      {viewMode !== "tpid" && selectedIndicator && rawIndicatorRows.length > 0 && selectedIndicator.toLowerCase() === "churn" && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Raw Data</Text>
            <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Churn signals contributing to score</Text>
          </Stack>
          <PurviewChurnRawIndicatorCards rows={rawIndicatorRows} />
        </Stack>
      )}

      {/* Community Engagement Raw Indicator */}
      {viewMode !== "tpid" && selectedIndicator && rawIndicatorRows.length > 0 && selectedIndicator.toLowerCase() === "community engagement" && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Raw Data</Text>
            <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Tenant-level community engagement</Text>
          </Stack>
          <PurviewCommunityEngagementRawIndicatorCards rows={rawIndicatorRows} />
        </Stack>
      )}

      {/* Feature Request Health Raw Indicator */}
      {viewMode !== "tpid" && selectedIndicator && /feature.health|feature.request/i.test(selectedIndicator) && featureHealthRows.length > 0 && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Raw Data</Text>
            <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Feature request counts by priority</Text>
          </Stack>
          <FeatureHealthRawIndicatorCards rows={featureHealthRows} />
        </Stack>
      )}

      {/* Support Health Score Raw Indicator */}
      {viewMode !== "tpid" && selectedIndicator && /support.health|shi.score/i.test(selectedIndicator) && shiRows.length > 0 && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Raw Data</Text>
            <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>Indicator level details and factors</Text>
          </Stack>
          <SHIScoreRawIndicatorCards rows={shiRows} />
        </Stack>
      )}

      {/* Generic Raw Data Fallback */}
      {selectedIndicator && rawData.length > 0
        && (viewMode === "tpid" || (
          selectedIndicator.toLowerCase() !== "deployment"
          && selectedIndicator.toLowerCase() !== "engagement"
          && selectedIndicator.toLowerCase() !== "churn"
          && selectedIndicator.toLowerCase() !== "community engagement"
          && !/feature.health|feature.request/i.test(selectedIndicator)
          && !/support.health|shi.score/i.test(selectedIndicator)
        )) && (
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              Raw Data
            </Text>
            <TooltipHost content={PurviewCustomerHealthTooltips.RawData}>
              <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "pointer" } }} />
            </TooltipHost>
            <Text variant="small" styles={{ root: { color: "#605E5C" } }}>
              Weighted contribution to product score
            </Text>
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 12 }} wrap styles={{ root: { width: "100%" } }}>
            {rawData.map((item, idx) => (
              <Stack
                key={`${item.label}-${idx}`}
                verticalFill
                styles={{
                  root: {
                    padding: "16px",
                    border: "1px solid #EDEBE9",
                    borderRadius: 8,
                    backgroundColor: "#FFFFFF",
                    width: 200,
                    minWidth: 200,
                    minHeight: 100,
                    justifyContent: "space-between",
                  },
                }}
              >
                <Text variant="medium" styles={{ root: { fontWeight: 600, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", lineHeight: "20px", minHeight: 40 } }}>
                  {formatPascalCase(item.label)}
                </Text>
                <Text variant="xLarge" styles={{ root: { fontWeight: item.label === "IsEngaged" ? 400 : 700 } }}>
                  {item.value}
                </Text>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
    </div>
  );
});

export default PurviewProductDetails;